/**
 * Checkout redirects identify what to check; only authenticated API responses
 * can establish rental access or make another checkout safe.
 */
export function getRentalReturnContext(search) {
  const params = new URLSearchParams(search);
  const attemptId = params.get('rental_attempt') || null;
  const payment = params.get('payment');

  // Older Stripe returns have no attempt reference. A cancelled Stripe
  // checkout without a reference is the ordinary watch page.
  if (!attemptId && payment !== 'success') return null;
  return { attemptId, payment };
}

/**
 * A checkout can discover existing access or reconcile an earlier payment.
 * Return to this film locally, rather than following a server-supplied URL.
 */
export function getLocalRentalRedirect(result, videoId) {
  if (typeof result.redirectUrl !== 'string' || !result.redirectUrl) throw new Error('Invalid rental redirect');
  const path = `/watch/${encodeURIComponent(videoId)}`;
  if (result.attemptId === undefined) return path;
  if (
    typeof result.attemptId !== 'string' ||
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(result.attemptId)
  ) {
    throw new Error('Invalid rental payment reference');
  }
  return `${path}?${new URLSearchParams({ rental_attempt: result.attemptId })}`;
}

async function boundedRequest(request, timeoutMs) {
  if (timeoutMs <= 0) throw new Error('Payment status check timed out');
  const controller = new AbortController();
  let timer;
  try {
    return await Promise.race([
      Promise.resolve().then(() => request(controller.signal)),
      new Promise((_, reject) => {
        timer = setTimeout(() => {
          controller.abort();
          reject(new Error('Payment status check timed out'));
        }, timeoutMs);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Poll the server for a bounded time while the page shows a pending panel.
 * A completed payment alone is insufficient: the access projection must also
 * be confirmed, since a webhook or database update may still be in flight.
 */
export async function resolveRentalPaymentReturn({
  videoId,
  context,
  reconcilePayment,
  checkAccess,
  maxChecks = 5,
  delayMs = 750,
  requestTimeoutMs = 2500,
  paymentRequestTimeoutMs = requestTimeoutMs,
  maxDurationMs = 45_000,
  wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  now = () => Date.now(),
}) {
  let lastResult = { state: 'pending', canStartCheckout: false };
  const startedAt = Date.now();
  const remainingTime = () => maxDurationMs - (Date.now() - startedAt);

  for (let check = 0; check < maxChecks; check++) {
    if (remainingTime() <= 0) break;
    try {
      const payment = context.attemptId
        ? await boundedRequest(
            (signal) => reconcilePayment(context.attemptId, videoId, signal),
            Math.min(paymentRequestTimeoutMs, remainingTime()),
          )
        : null;
      if (payment && !['pending', 'completed', 'refunded', 'failed', 'cancelled'].includes(payment.state)) {
        throw new Error('Unknown payment status');
      }

      const access = await boundedRequest(
        (signal) => checkAccess(videoId, signal),
        Math.min(requestTimeoutMs, remainingTime()),
      );
      if (typeof access?.hasAccess !== 'boolean') throw new Error('Unknown rental access status');

      if (access.hasAccess) {
        return { state: 'access', canStartCheckout: false, rental: access.rental || null };
      }

      // Neither the redirect's "cancelled" marker nor a network failure is
      // evidence that a charge failed. Only a verified API state permits retry.
      if (payment?.state === 'cancelled') {
        return { state: 'unpaid', canStartCheckout: true };
      }
      if (payment?.state === 'refunded') return { state: 'refunded', canStartCheckout: true };
      if (payment?.state === 'completed' && Number.isFinite(payment.expiresAt) && payment.expiresAt <= now()) {
        return { state: 'expired', canStartCheckout: true };
      }

      // Failed charges can succeed on a later retry. Resume only a verified
      // pending attempt, using the same order, rather than creating a new one.
      lastResult = {
        state: 'pending',
        canStartCheckout: false,
        ...(payment?.state === 'pending' && context.attemptId ? { canResumeCheckout: true } : {}),
      };
    } catch {
      // Keep the attempt recoverable; a failed lookup can hide a paid charge.
      lastResult = { state: 'error', canStartCheckout: false };
    }

    if (check + 1 < maxChecks && remainingTime() > 0) await wait(Math.min(delayMs, remainingTime()));
  }

  return lastResult;
}
